import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Ellipsis,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  toast,
} from "@atlant/ui"

import { AircraftUpdateImagesDocument, PublicFileUploadDocument } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { useAircraftSource } from "#pages/authenticated/fleet/[aircraftId]/use-aircraft-source.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getFileUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"

type ViewMode = "grid" | "select" | "viewer"

interface SortableImageProps {
  url: string
  src: string
  onClick: () => void
}

const SortableImage = ({ url, src, onClick }: SortableImageProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: url })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative aspect-4/3 cursor-pointer overflow-hidden rounded-lg"
      onClick={onClick}
    >
      <img src={src} alt="" className="size-full object-cover" />
      <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
      <button
        type="button"
        className="absolute top-2 left-2 cursor-grab rounded-md bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70 active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
    </div>
  )
}

interface SelectableImageProps {
  src: string
  selected: boolean
  onToggle: () => void
}

const SelectableImage = ({ src, selected, onToggle }: SelectableImageProps) => (
  <div
    className={`relative aspect-4/3 cursor-pointer overflow-hidden rounded-lg ring-2 transition-all ${
      selected ? "ring-primary" : "ring-transparent"
    }`}
    onClick={onToggle}
  >
    <img src={src} alt="" className="size-full object-cover" />
    {selected && <div className="bg-primary/10 absolute inset-0" />}
    <div
      className={`absolute top-2 left-2 flex size-6 items-center justify-center rounded-md border-2 transition-colors ${
        selected ? "border-primary bg-primary text-white" : "border-white/80 bg-black/30"
      }`}
    >
      {selected && <Check className="size-3.5" strokeWidth={3} />}
    </div>
  </div>
)

interface AircraftImageGalleryModalProps {
  open: boolean
  onClose: () => void
  aircraftId: string
  images: string[]
  tail: string
  manufacturer: string
  model: string
  serial: string
  yom: string
}

const MAX_FILES = 16

export const AircraftImageGalleryModal = ({
  open,
  onClose,
  aircraftId,
  images: externalImages,
  tail,
  manufacturer,
  model,
  serial,
  yom,
}: AircraftImageGalleryModalProps) => {
  const { me } = useLoggedInUserInfo()
  const source = useAircraftSource()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [images, setImages] = useState(externalImages)
  const [mode, setMode] = useState<ViewMode>("grid")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [viewerIndex, setViewerIndex] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    setImages(externalImages)
  }, [externalImages])

  useEffect(() => {
    if (!open) {
      setMode("grid")
      setSelected(new Set())
      setViewerIndex(0)
    }
  }, [open])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: (variables: { name: string; file: File }) =>
      client.request(PublicFileUploadDocument, {
        input: {
          organizationId: me.organization.id,
          name: variables.name,
          file: variables.file,
          entity: "aircraft",
        },
      }),
  })

  const { mutateAsync: updateImages } = useMutation({
    mutationFn: (newImages: string[]) =>
      client.request(AircraftUpdateImagesDocument, {
        input: {
          id: aircraftId,
          organizationId: me.organization.id,
          images: newImages,
          source,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.aircraft.detail(aircraftId) })
    },
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = images.indexOf(active.id as string)
    const newIndex = images.indexOf(over.id as string)
    const reordered = arrayMove(images, oldIndex, newIndex)
    setImages(reordered)
    void updateImages(reordered)
  }

  const handleBulkDelete = async () => {
    const filtered = images.filter((img) => !selected.has(img))
    setImages(filtered)
    setSelected(new Set())
    setConfirmOpen(false)
    setMode("grid")
    await updateImages(filtered)
  }

  const handleUpload = async (files: FileList) => {
    const allowed = MAX_FILES - images.length
    if (allowed <= 0) {
      toast.error(`Maximum ${MAX_FILES} images allowed`)
      return
    }
    const fileArray = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, allowed)
    if (fileArray.length === 0) return

    setIsUploading(true)
    const uploadedUrls: string[] = []
    for (const file of fileArray) {
      try {
        const result = await uploadFile({ name: file.name, file })
        uploadedUrls.push(result.publicFileUpload)
      } catch {
        toast.error(`Failed to upload ${file.name}`)
      }
    }
    if (uploadedUrls.length > 0) {
      const newImages = [...images, ...uploadedUrls]
      setImages(newImages)
      await updateImages(newImages)
      toast.success(
        `${uploadedUrls.length} image${uploadedUrls.length > 1 ? "s" : ""} uploaded`,
      )
    }
    setIsUploading(false)
  }

  const toggleSelect = useCallback((url: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(url)) next.delete(url)
      else next.add(url)
      return next
    })
  }, [])

  const selectAll = () => setSelected(new Set(images))

  const enterSelectMode = () => {
    setSelected(new Set())
    setMode("select")
  }

  const exitSelectMode = () => {
    setSelected(new Set())
    setMode("grid")
  }

  const openViewer = (index: number) => {
    setViewerIndex(index)
    setMode("viewer")
  }

  const getSrc = (url: string) => getFileUrl(me.organization.id, "aircraft", url)

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="p-0 sm:max-w-6xl">
          <DialogHeader className="border-b p-5">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-lg font-bold">{tail}</DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm">
                  {manufacturer} {model} · S/N {serial} · {yom}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex h-[65vh] flex-col">
            <AnimatePresence mode="wait" initial={false}>
              {mode === "viewer" ? (
                <motion.div
                  key="viewer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-h-0 flex-1 flex-col"
                >
                  <ViewerContent
                    images={images}
                    index={viewerIndex}
                    onIndexChange={setViewerIndex}
                    getSrc={getSrc}
                    onBack={() => setMode("grid")}
                    onUpload={() => inputRef.current?.click()}
                    onDelete={() => {
                      const img = images[viewerIndex]
                      if (!img) return
                      setSelected(new Set([img]))
                      setConfirmOpen(true)
                    }}
                    isUploading={isUploading}
                    canUpload={images.length < MAX_FILES}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-h-0 flex-1 flex-col"
                >
                  <div className="px-5 pt-3 pb-1">
                    {mode === "select" ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={exitSelectMode}
                          >
                            Cancel
                          </Button>
                          <div className="text-muted-foreground flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs">
                            <span>
                              Drag and drop to reorder photos. Select photos to delete.
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-sm">
                            {selected.size} selected
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                            disabled={selected.size === 0}
                            onClick={() => setConfirmOpen(true)}
                          >
                            <Trash2 className="size-3.5" />
                            Delete ({selected.size})
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={selectAll}
                          >
                            Select all
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          className="h-8 gap-1.5"
                          onClick={() => inputRef.current?.click()}
                          disabled={isUploading || images.length >= MAX_FILES}
                        >
                          <Plus className="size-3.5" />
                          {isUploading ? "Uploading..." : "Add photo"}
                        </Button>
                        {images.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="size-8 p-0">
                                <Ellipsis className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem
                                onClick={enterSelectMode}
                                className="gap-2"
                              >
                                <Trash2 className="size-4" />
                                Select & delete photos
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto p-5 pt-3">
                    {images.length === 0 ? (
                      <div className="flex h-full flex-col items-center justify-center text-center">
                        <p className="text-muted-foreground text-sm">No photos yet</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => inputRef.current?.click()}
                        >
                          Upload photos
                        </Button>
                      </div>
                    ) : mode === "select" ? (
                      <div className="grid grid-cols-4 gap-3">
                        {images.map((url) => (
                          <SelectableImage
                            key={url}
                            src={getSrc(url)}
                            selected={selected.has(url)}
                            onToggle={() => toggleSelect(url)}
                          />
                        ))}
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext items={images} strategy={rectSortingStrategy}>
                          <div className="grid grid-cols-4 gap-3">
                            {images.map((url, i) => (
                              <SortableImage
                                key={url}
                                url={url}
                                src={getSrc(url)}
                                onClick={() => openViewer(i)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) {
            void handleUpload(e.target.files)
            e.target.value = ""
          }
        }}
      />

      <AlertDialog open={confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(false)}>
        <AlertDialogContent className="sm:max-w-sm">
          <AlertDialogHeader className="items-center text-center">
            <div className="mx-auto mb-2 flex size-14 items-center justify-center rounded-full bg-red-50">
              <Trash2 className="size-6 text-red-500" />
            </div>
            <AlertDialogTitle>Delete selected images?</AlertDialogTitle>
            <AlertDialogDescription>
              {selected.size === 1
                ? "This image will be permanently removed from the album. This action can't be undone."
                : `These ${selected.size} images will be permanently removed from the album. This action can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="flex-1"
              onClick={() => void handleBulkDelete()}
            >
              Delete images
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

const ViewerContent = ({
  images,
  index,
  onIndexChange,
  getSrc,
  onBack,
  onUpload,
  onDelete,
  isUploading,
  canUpload,
}: {
  images: string[]
  index: number
  onIndexChange: (i: number) => void
  getSrc: (url: string) => string
  onBack: () => void
  onUpload: () => void
  onDelete: () => void
  isUploading: boolean
  canUpload: boolean
}) => {
  const hasPrev = index > 0
  const hasNext = index < images.length - 1

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasPrev) onIndexChange(index - 1)
      if (e.key === "ArrowRight" && hasNext) onIndexChange(index + 1)
      if (e.key === "Escape") onBack()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [index, hasPrev, hasNext, onIndexChange, onBack])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-5 pt-3 pb-1">
        <Button
          variant="default"
          size="sm"
          className="h-8 gap-1.5"
          onClick={onUpload}
          disabled={isUploading || !canUpload}
        >
          <Plus className="size-3.5" />
          {isUploading ? "Uploading..." : "Add photo"}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="size-8" onClick={onDelete}>
            <Trash2 className="size-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={onBack}>
            <ArrowLeft className="size-3.5" />
            Back to grid
          </Button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-12">
        <button
          type="button"
          onClick={() => hasPrev && onIndexChange(index - 1)}
          disabled={!hasPrev}
          className="absolute left-3 flex size-9 items-center justify-center rounded-full bg-black/5 text-gray-500 transition-colors hover:bg-black/10 disabled:opacity-30"
        >
          <ChevronLeft className="size-5" />
        </button>

        <AnimatePresence mode="wait" initial={false}>
          <motion.img
            key={images[index] ?? index}
            src={getSrc(images[index] ?? "")}
            alt=""
            className="max-h-full max-w-full rounded-lg object-contain"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.15 }}
          />
        </AnimatePresence>

        <button
          type="button"
          onClick={() => hasNext && onIndexChange(index + 1)}
          disabled={!hasNext}
          className="absolute right-3 flex size-9 items-center justify-center rounded-full bg-black/5 text-gray-500 transition-colors hover:bg-black/10 disabled:opacity-30"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      <div className="flex flex-col items-center gap-3 px-5 pb-5">
        <span className="text-muted-foreground text-xs">
          {index + 1}/{images.length}
        </span>
        <div className="flex gap-1.5 overflow-x-auto">
          {images.map((url, i) => (
            <button
              key={url}
              type="button"
              onClick={() => onIndexChange(i)}
              className={`shrink-0 rounded-md border-2 p-0.5 transition-all ${
                i === index
                  ? "border-primary"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={getSrc(url)} alt="" className="size-14 rounded object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
